import React, { forwardRef } from "react";

import { Input, InputProps } from "../Input/Input";

export interface TextAreaProps extends Omit<InputProps, "multiline" | "type"> {
  /** Starting height in rows (the box never shrinks below this). */
  minRows?: number;
  /** Cap before the box stops growing and starts scrolling. */
  maxRows?: number;
}

/**
 * Multi-line text field. Same surface/focus treatment as `Input`, and the box
 * auto-grows from `minRows` up to `maxRows` as you type (then scrolls). Use this
 * anywhere a note / description / free-text field needs more than one line, in
 * preference to `<Input multiline />` — this is the named component for the case.
 *
 * It carries NO styling of its own. It used to re-fix the top-aligned placeholder
 * here with its own padding overrides, which meant the multiline treatment had two
 * owners: a consumer reaching for `<Input multiline />` directly got the unfixed
 * version, and the two could drift. `Input` now owns the multiline padding (§31),
 * so both spellings render identically and this is purely a preset.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ minRows = 3, maxRows = 12, sx, ...props }, ref) => (
    <Input
      {...props}
      ref={ref as unknown as React.Ref<HTMLInputElement>}
      multiline
      minRows={minRows}
      maxRows={maxRows}
      sx={sx}
    />
  )
);

TextArea.displayName = "TextArea";
